import React from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Checkbox, Skeleton, Table } from '@gaiads/telia-react-component-library';
import { BundleContentOverview, LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import { BundleListItemType } from '../../../store/adminBundles';
import BundleListItem from './BundleListItem';
import VariantListItem from './VariantListItem';
import MainListItem from './MainListItem';

type TableHeader = string | undefined;
type BundleTableHeaders = [TableHeader, TableHeader, TableHeader, TableHeader, TableHeader, TableHeader, TableHeader, TableHeader, TableHeader];
const tableHeaders: BundleTableHeaders = [
  undefined,
  undefined,
  'bundleBrand',
  'bundle_name',
  undefined,
  'sku',
  'languages',
  'sales_channels',
  undefined,
];

export interface BundlesListProps {
  bundleListItems: BundleListItemType[];
  locales: LocaleResponse[];
  selectAll: boolean;
  onBundleChecked: (bundleId: number) => void;
  onAllChecked: () => void;
  onClickRow: (bundleId: number) => void;
  onOpenRow: (bundleId: number) => void;
  isSelected: (bundleId: number) => boolean;
  isOpened: (bundleId: number) => boolean;
  isLoaded: (bundleId: number) => boolean;
}
interface RenderVariantItemsProps {
  itemContent: BundleContentOverview | undefined;
  isOpen: boolean;
  isLoaded: boolean;
}
const RenderVariantItems = ({ itemContent, isOpen, isLoaded }: RenderVariantItemsProps) => {
  if (isOpen && !isLoaded) {
    return (
      <Table.Row className="daas-product-table__skeleton-row" data-testid="product-table-skeleton-row">
        <Table.Cell colSpan={10}>
          <Skeleton widthInPercentages={100} height={88} />
        </Table.Cell>
      </Table.Row>
    );
  } else if (isOpen && isLoaded && itemContent) {
    return (
      <>
        {itemContent.main_item_options.map(mainItem => (
          <MainListItem key={`main-item-${mainItem.id}`} item={mainItem} />
        ))}
        {itemContent.items.map(addonGroup => (
          <VariantListItem key={`addon-${addonGroup.id}`} item={addonGroup} groupName={addonGroup.name} />
        ))}
      </>
    );
  } else {
    return <></>;
  }
};

const BundlesList = (props: BundlesListProps) => {
  const { locales, selectAll, bundleListItems, onAllChecked, onBundleChecked, onClickRow, onOpenRow, isSelected, isOpened, isLoaded } = props;
  const { t } = useTranslation('bundlesview');
  return (
    <Table className={'products-table'}>
      <thead>
        <Table.Row>
          <Table.HeaderCell type="checkbox">
            <Checkbox
              checked={selectAll}
              onChange={() => {
                onAllChecked();
              }}
            />
          </Table.HeaderCell>
          {tableHeaders.map((header, index) => (
            <Table.HeaderCell key={`table-header-${index}`}>
              <BodyText fontWeight="bold">{header && t(`bundles_table.${header}`)}</BodyText>
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </thead>
      <tbody>
        {bundleListItems.map((item: BundleListItemType, index) => (
          <BundleListItem
            key={index}
            item={item}
            locales={locales}
            open={isOpened(item.id)}
            onOpenRow={onOpenRow}
            onItemChecked={onBundleChecked}
            onRowClick={onClickRow}
            selected={isSelected(item.id)}
          >
            <RenderVariantItems itemContent={item.content} isOpen={isOpened(item.id)} isLoaded={isLoaded(item.id)} />
          </BundleListItem>
        ))}
      </tbody>
    </Table>
  );
};

export default BundlesList;
